import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { gql, useClient, useQuery } from "urql";
import { useDropzone } from "react-dropzone";
import { Formik, Form, useFormikContext } from "formik";
import { sortBy } from "lodash";
import { useLocation, useParams } from "wouter";
import { Alert, AlertVariant } from "../../../../components/src/Alert";
import { Button } from "../../../../components/src/Button";
import { CheckboxItem, SelectField } from "../../../../components/src/form";
import { userHaveAccessTo } from "../../common/Permissions";
import { PermissionAssetEnum } from "../../types.generated";
import { GET_ME } from "../scheduler/JobsPage";
import { DownloadFileModal } from "./DownloadFileModal";
import { FundParseClientFields } from "./FundParseClientFields";
import { FundParseClientSelector } from "./FundParseClientSelector";
import { FundParseDateField } from "./FundParseDateField";
import { FundParseConsoleLine, FundParseJobConsole } from "./FundParseJobConsole";
import { FundParseExposureCharts } from "./FundParseExposureCharts";
import { FundParseOutputPanel } from "./FundParseOutputPanel";
import { FundParsePositionMappingTable } from "./FundParsePositionMappingTable";
import { pollFundParseJobUntilDone, runFundParseJob } from "./fundParseApi";
import { processFundParseFromSavedReport, validateLoadFromSavedReport } from "./fundParseFromReport";
import {
    FUND_PARSE_JOB_NAME,
    FundParseFormValues
} from "./fundParseDefaults";
import { resolveFundParseDate } from "./fundParseDateUtils";
import { GET_TPT_REPORT_SUMMARY } from "./fundParseQueries";
import { buildFundParsePath, getFundParseFormValuesFromRoute } from "./fundParseUrl";
import { FundParseInputFile, FundParseJobHistoryResult, FundParseOutputFile, FundParseExposureChartsSummary, FundParsePositionMappingSummary } from "./fundParseTypes";
import {
    buildFundParseJobArgs,
    createConsoleLineId,
    createInputFileId,
    downloadOutputFile,
    extractFundParseJobErrorMessages,
    extractFundParseJobWarningMessages,
    formatAxiosFundParseError,
    formatFundParseJobFailureAlert,
    formatJobStatusMessage,
    getDownloadableOutputFiles,
    isFundParseJobFailed,
    isFundParseUnknownCicWarning,
    parseExposureChartsSummary,
    parseFundParseOutputFiles,
    parseFundParseOutputManifest,
    parseJobStdoutStderr,
    parsePimcoParsedInputFileNames,
    parsePositionMappingSummary,
    readFileAsBase64,
    revealStdoutLines,
    validateFundParseRun,
    formValuesForRun
} from "./fundParseUtils";

const GET_CLIENTS = gql`
    query fundParseClients($typeIn: [PartyType!], $statusIn: [PartyStatusEnum!]) {
        clients: parties(filter: { typeIn: $typeIn, statusIn: $statusIn }) {
            _id
            name
        }
    }
`;

interface FundParseRouteDateSyncProps {
    clientId: string;
    routeDate?: string;
    disabled: boolean;
}

function FundParseRouteDateSync({ clientId, routeDate, disabled }: FundParseRouteDateSyncProps): null {
    const [, navigate] = useLocation();
    const { values } = useFormikContext<FundParseFormValues>();

    useEffect(() => {
        if (disabled || !clientId || !values.date) {
            return;
        }

        let resolvedDate: string;
        try {
            resolvedDate = resolveFundParseDate(values.date);
        } catch {
            return;
        }

        if (resolvedDate !== routeDate) {
            navigate(buildFundParsePath(clientId, resolvedDate));
        }
    }, [clientId, disabled, navigate, routeDate, values.date]);

    return null;
}

export function FundParsePage(): React.ReactElement {
    const { clientId: routeClientId, date: routeDate } = useParams<{ clientId?: string; date?: string }>();
    const [, navigate] = useLocation();
    const [inputFiles, setInputFiles] = useState<FundParseInputFile[]>([]);
    const [outputFiles, setOutputFiles] = useState<FundParseOutputFile[]>([]);
    const [consoleLines, setConsoleLines] = useState<FundParseConsoleLine[]>([]);
    const [jobStatus, setJobStatus] = useState<string>("");
    const [isRunning, setIsRunning] = useState(false);
    const [isLoadingFromReport, setIsLoadingFromReport] = useState(false);
    const [selectedOutputFile, setSelectedOutputFile] = useState<FundParseOutputFile | null>(null);
    const [pimcoParsedFileNames, setPimcoParsedFileNames] = useState<Set<string>>(() => new Set());
    const [positionMappingSummary, setPositionMappingSummary] = useState<FundParsePositionMappingSummary | null>(null);
    const [exposureChartsSummary, setExposureChartsSummary] = useState<FundParseExposureChartsSummary | null>(null);
    const [alert, setAlert] = useState<{ color: AlertVariant; visible: boolean; message: string }>({
        color: "info",
        visible: false,
        message: ""
    });
    const lastReportedStatusRef = useRef<string>("");
    const streamedStdoutCountRef = useRef(0);

    const appendConsoleLine = useCallback((type: FundParseConsoleLine["type"], text: string) => {
        setConsoleLines((current) => [...current, { id: createConsoleLineId(type), type, text }]);
    }, []);

    const appendStdoutLines = useCallback(
        (lines: string[]) => {
            if (!lines.length) {
                return;
            }
            setConsoleLines((current) => [
                ...current,
                ...lines.map((text) => ({ id: createConsoleLineId("stdout"), type: "stdout" as const, text }))
            ]);
        },
        []
    );

    const appendStderrLines = useCallback(
        (lines: string[]) => {
            if (!lines.length) {
                return;
            }
            setConsoleLines((current) => [
                ...current,
                ...lines.map((text) => ({ id: createConsoleLineId("stderr"), type: "stderr" as const, text }))
            ]);
        },
        []
    );

    const updatePimcoLabelsFromStdout = useCallback((stdoutLines: string[]) => {
        setPimcoParsedFileNames(new Set(parsePimcoParsedInputFileNames(stdoutLines)));
    }, []);

    const syncOutputArtifacts = useCallback((history: FundParseJobHistoryResult) => {
        const parsedOutputFiles = parseFundParseOutputFiles(history.output);
        setOutputFiles(parsedOutputFiles);
        setPositionMappingSummary(parsePositionMappingSummary(parsedOutputFiles));
        setExposureChartsSummary(parseExposureChartsSummary(parsedOutputFiles));
    }, []);

    const syncPartialJobOutput = useCallback(
        (history: FundParseJobHistoryResult) => {
            const nextStatus = history.status || "";
            if (nextStatus && nextStatus !== lastReportedStatusRef.current) {
                lastReportedStatusRef.current = nextStatus;
                appendConsoleLine("status", formatJobStatusMessage(nextStatus));
            }

            const { stdoutLines } = parseJobStdoutStderr(history.output);
            updatePimcoLabelsFromStdout(stdoutLines);
            syncOutputArtifacts(history);
            if (stdoutLines.length > streamedStdoutCountRef.current) {
                const newLines = stdoutLines.slice(streamedStdoutCountRef.current);
                streamedStdoutCountRef.current = stdoutLines.length;
                appendStdoutLines(newLines);
            }
        },
        [appendConsoleLine, appendStdoutLines, syncOutputArtifacts, updatePimcoLabelsFromStdout]
    );

    const gqlClient = useClient();

    const [{ data: dataMe, fetching: loadingMe, error: errorMe }] = useQuery({
        query: GET_ME,
        requestPolicy: "cache-and-network"
    });

    const [{ fetching: fetchingClients, error: errorClients, data: dataClients }] = useQuery({
        query: GET_CLIENTS,
        variables: { typeIn: "Client", statusIn: "Confirmed" },
        requestPolicy: "cache-and-network"
    });

    const clientOptions = useMemo(() => {
        if (!dataClients?.clients) {
            return [];
        }
        return sortBy(
            dataClients.clients.map((client) => ({ key: client._id, value: client.name, text: client.name })),
            "value"
        );
    }, [dataClients]);

    const initialFormValues = useMemo(
        () => getFundParseFormValuesFromRoute(routeClientId, routeDate),
        [routeClientId, routeDate]
    );

    const resolvedRouteDate = useMemo(() => {
        if (!routeClientId) {
            return null;
        }
        try {
            return resolveFundParseDate(routeDate || initialFormValues.date);
        } catch {
            return null;
        }
    }, [routeClientId, routeDate, initialFormValues.date]);

    const [{ data: tptReportData, fetching: fetchingTptReport }] = useQuery({
        query: GET_TPT_REPORT_SUMMARY,
        variables: { clientId: routeClientId, date: resolvedRouteDate },
        pause: !routeClientId || !resolvedRouteDate,
        requestPolicy: "cache-and-network"
    });

    const existingTptReport = tptReportData?.reports?.[0];

    useEffect(() => {
        if (routeClientId && !routeDate) {
            navigate(buildFundParsePath(routeClientId));
        }
    }, [routeClientId, routeDate, navigate]);

    const handleClientChange = useCallback(
        (clientId: string) => {
            if (!clientId) {
                navigate("/fundparse");
                return;
            }
            navigate(buildFundParsePath(clientId, routeDate || initialFormValues.date));
        },
        [navigate, routeDate, initialFormValues.date]
    );

    const outputManifest = useMemo(() => parseFundParseOutputManifest(outputFiles), [outputFiles]);

    const onDrop = useCallback(async (acceptedFiles: File[]) => {
        if (!acceptedFiles.length) {
            return;
        }

        try {
            const uploadedFiles = await Promise.all(
                acceptedFiles.map(async (file) => {
                    const payload = await readFileAsBase64(file);
                    return {
                        id: createInputFileId(file.name, file.size),
                        ...payload
                    };
                })
            );

            setInputFiles((current) => {
                const existingNames = new Set(current.map((file) => file.fileName));
                const nextFiles = uploadedFiles.filter((file) => !existingNames.has(file.fileName));
                return [...current, ...nextFiles];
            });
            setAlert({ color: "success", visible: true, message: `Added ${acceptedFiles.length} input file(s).` });
        } catch (error) {
            setAlert({
                color: "danger",
                visible: true,
                message: error instanceof Error ? error.message : "Failed to read uploaded file."
            });
        }
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"]
        },
        multiple: true
    });

    const handleLoadFromSavedReport = async (valuesToRun: FundParseFormValues) => {
        const runValues = formValuesForRun(valuesToRun, routeClientId);
        const validationError = validateLoadFromSavedReport(runValues);
        if (validationError) {
            setAlert({ color: "danger", visible: true, message: validationError });
            return;
        }

        setIsLoadingFromReport(true);
        setConsoleLines([]);
        setPimcoParsedFileNames(new Set());
        setPositionMappingSummary(null);
        setExposureChartsSummary(null);
        setJobStatus("");
        setOutputFiles([]);
        appendConsoleLine("status", "Loading saved TPT report from database...");
        setAlert({ color: "info", visible: true, message: "Loading saved TPT report and generating outputs..." });

        try {
            const result = await processFundParseFromSavedReport(gqlClient, runValues);
            const parsedOutputFiles = result.outputFiles;
            setOutputFiles(parsedOutputFiles);
            setPositionMappingSummary(parsePositionMappingSummary(parsedOutputFiles));
            setExposureChartsSummary(parseExposureChartsSummary(parsedOutputFiles));
            appendStdoutLines(result.consoleLines);
            setJobStatus("completed");
            appendConsoleLine("status", formatJobStatusMessage("completed"));
            const warningMessages = result.consoleLines.filter((line) => isFundParseUnknownCicWarning(line));
            const downloadableCount = getDownloadableOutputFiles(
                parsedOutputFiles,
                parseFundParseOutputManifest(parsedOutputFiles)
            ).length;
            let completionMessage = parsedOutputFiles.length
                ? `Loaded saved report. ${downloadableCount} downloadable output file(s) available.`
                : "Loaded saved report.";
            if (warningMessages.length) {
                completionMessage = `${warningMessages.join("\n")}\n\n${completionMessage}`;
            }
            setAlert({
                color: warningMessages.length ? "danger" : "success",
                visible: true,
                message: completionMessage
            });
        } catch (error) {
            setJobStatus("failed");
            const errorMessages = error instanceof Error ? [error.message] : ["Failed to load saved TPT report."];
            appendStderrLines(errorMessages);
            appendConsoleLine("status", formatJobStatusMessage("failed"));
            setAlert({ color: "danger", visible: true, message: errorMessages.join("\n") });
        } finally {
            setIsLoadingFromReport(false);
        }
    };

    const handleRunJob = async (valuesToRun: FundParseFormValues) => {
        const runValues = formValuesForRun(valuesToRun, routeClientId);
        const validationError = validateFundParseRun(runValues, inputFiles);
        if (validationError) {
            setAlert({ color: "danger", visible: true, message: validationError });
            return;
        }

        setIsRunning(true);
        setConsoleLines([]);
        setPimcoParsedFileNames(new Set());
        setPositionMappingSummary(null);
        setJobStatus("starting");
        setOutputFiles([]);
        lastReportedStatusRef.current = "";
        streamedStdoutCountRef.current = 0;
        appendConsoleLine("status", formatJobStatusMessage("starting"));
        setAlert({ color: "info", visible: true, message: `Starting ${FUND_PARSE_JOB_NAME}...` });

        try {
            const args = buildFundParseJobArgs(runValues, inputFiles);
            const runResponse = await runFundParseJob(args);
            appendConsoleLine("status", `Submitted job ${runResponse.id}.`);
            setJobStatus("running");

            const history = await pollFundParseJobUntilDone(runResponse.id, {
                onUpdate: (result) => {
                    setJobStatus(result.status || "running");
                    syncPartialJobOutput(result);
                }
            });

            setJobStatus(history.status || "unknown");
            syncPartialJobOutput(history);

            const { stdoutLines, stderrLines } = parseJobStdoutStderr(history.output);
            updatePimcoLabelsFromStdout(stdoutLines);
            const remainingStdout = stdoutLines.slice(streamedStdoutCountRef.current);
            streamedStdoutCountRef.current = stdoutLines.length;

            await revealStdoutLines(remainingStdout, (line) => {
                appendStdoutLines([line]);
            });

            const parsedOutputFiles = parseFundParseOutputFiles(history.output);
            setOutputFiles(parsedOutputFiles);
            setPositionMappingSummary(parsePositionMappingSummary(parsedOutputFiles));

            if (isFundParseJobFailed(history)) {
                const errorMessages = extractFundParseJobErrorMessages(history);
                appendStderrLines(stderrLines.filter((line) => !isFundParseUnknownCicWarning(line)));
                if (errorMessages.length > stderrLines.length) {
                    appendStderrLines(errorMessages.filter((message) => !stderrLines.includes(message)));
                }
                setAlert({ color: "danger", visible: true, message: formatFundParseJobFailureAlert(history) });
                return;
            }

            const warningMessages = extractFundParseJobWarningMessages(history);
            const downloadableCount = getDownloadableOutputFiles(
                parsedOutputFiles,
                parseFundParseOutputManifest(parsedOutputFiles)
            ).length;
            let completionMessage = parsedOutputFiles.length
                ? `Job completed. ${downloadableCount} downloadable output file(s) available.`
                : "Job completed.";
            if (warningMessages.length) {
                completionMessage = `${warningMessages.join("\n")}\n\n${completionMessage}`;
            }
            setAlert({
                color: warningMessages.length ? "danger" : "success",
                visible: true,
                message: completionMessage
            });
        } catch (error) {
            setJobStatus("failed");
            const errorMessages = formatAxiosFundParseError(error);
            appendStderrLines(errorMessages);
            appendConsoleLine("status", formatJobStatusMessage("failed"));
            setAlert({ color: "danger", visible: true, message: errorMessages.join("\n") });
        } finally {
            setIsRunning(false);
        }
    };

    if (loadingMe || fetchingClients) {
        return <p>Loading...</p>;
    }

    if (errorMe) {
        return <pre>{JSON.stringify(errorMe, null, 2)}</pre>;
    }

    if (!userHaveAccessTo("Any", PermissionAssetEnum.Job, dataMe.me.roles)) {
        return <div />;
    }

    if (errorClients) {
        return <pre>{JSON.stringify(errorClients, null, 2)}</pre>;
    }

    return (
        <div className="p-4">
            <DownloadFileModal
                file={selectedOutputFile}
                onClose={() => setSelectedOutputFile(null)}
                onDownload={downloadOutputFile}
            />

            <FundParseClientSelector
                clientOptions={clientOptions}
                value={routeClientId || ""}
                disabled={isRunning || isLoadingFromReport}
                onChange={handleClientChange}
            />

            {routeClientId && resolvedRouteDate && !fetchingTptReport && existingTptReport ? (
                <p className="mb-4 text-sm text-green-700">
                    A TPT report exists for this client and date
                    {existingTptReport.clientName ? ` (${existingTptReport.clientName}, ${existingTptReport.date})` : "."}
                </p>
            ) : null}

            <h1 className="mb-2 text-2xl font-semibold">Fund Parse</h1>
            <p className="mb-6 text-sm text-gray-600">
                Run the <strong>{FUND_PARSE_JOB_NAME}</strong> job with uploaded TPT .xlsx files, or load outputs directly
                from a saved TPT report in the database. Use <strong>Run job</strong> with <em>Create report</em> to parse
                uploads and optionally save a consolidated TPT report; use <em>Read report</em> to regenerate outputs from
                the stored report. <strong>Load from saved report</strong> performs the read-report processing in TypeScript
                without calling the Python job server.
            </p>

            {alert.visible ? (
                <Alert variant={alert.color} onClose={() => setAlert({ ...alert, visible: false })} dismissible className="mb-4">
                    <span className="whitespace-pre-wrap">{alert.message}</span>
                </Alert>
            ) : null}

            <div className="mb-8 rounded-lg border border-gray-200 bg-gray-50 p-4">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <h2 className="text-lg font-medium">Input files</h2>
                    <Button variant="secondary" size="sm" onClick={() => {
                        setInputFiles([]);
                        setPimcoParsedFileNames(new Set());
                    }} disabled={!inputFiles.length || isRunning || isLoadingFromReport}>
                        Clear input files
                    </Button>
                </div>
                <div
                    {...getRootProps()}
                    className={`min-h-[10rem] rounded border-2 border-dashed p-6 text-center transition-colors ${
                        isDragActive ? "border-sky-500 bg-sky-50" : "border-gray-300 bg-white"
                    }`}
                >
                    <input {...getInputProps()} />
                    <p>{isDragActive ? "Drop .xlsx files here..." : "Drag & drop .xlsx files here, or click to select"}</p>
                    <p className="mt-2 text-sm text-gray-500">You can add multiple files at once or one by one.</p>
                </div>
                {inputFiles.length ? (
                    <ul className="mt-4 space-y-2">
                        {inputFiles.map((file) => (
                            <li
                                key={file.id}
                                className="flex items-center justify-between rounded border border-gray-200 bg-white px-3 py-2 text-sm"
                            >
                                <span className="flex min-w-0 flex-wrap items-center gap-2">
                                    <span className="truncate">
                                        {file.fileName} ({Math.round(file.size / 1024)} KB)
                                    </span>
                                    {pimcoParsedFileNames.has(file.fileName) ? (
                                        <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900">
                                            Pimco TPT
                                        </span>
                                    ) : null}
                                </span>
                                <Button
                                    variant="danger"
                                    size="sm"
                                    disabled={isRunning || isLoadingFromReport}
                                    onClick={() => setInputFiles((current) => current.filter((item) => item.id !== file.id))}
                                >
                                    Remove
                                </Button>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p className="mt-3 text-sm text-gray-500">No input files selected.</p>
                )}
            </div>

            <Formik enableReinitialize initialValues={initialFormValues} onSubmit={() => {}}>
                {({ values, setValues, isSubmitting }) => (
                    <Form autoComplete="off">
                        <FundParseRouteDateSync
                            clientId={routeClientId || ""}
                            routeDate={routeDate}
                            disabled={isRunning || isLoadingFromReport}
                        />
                        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                            <h2 className="text-lg font-medium">Job arguments</h2>
                            <Button
                                variant="secondary"
                                size="sm"
                                type="button"
                                disabled={isRunning || isLoadingFromReport}
                                onClick={() => {
                                    setValues(getFundParseFormValuesFromRoute(routeClientId, routeDate));
                                }}
                            >
                                Reset arguments
                            </Button>
                        </div>

                        <div className="grid [grid-template-columns:repeat(auto-fill,minmax(240px,1fr))] gap-x-2 gap-y-4">
                            <div className="min-w-0">
                                <SelectField
                                    name="database"
                                    label="Database"
                                    className=""
                                    disabled={isRunning || isLoadingFromReport}
                                    options={["prod", "test"]}
                                />
                            </div>
                            <div className="min-w-0">
                                <FundParseDateField disabled={isRunning || isLoadingFromReport} />
                            </div>
                            <FundParseClientFields clientOptions={clientOptions} disabled={isRunning || isLoadingFromReport} />
                            <div className="min-w-0">
                                <CheckboxItem name="create_report" label="Create report" disabled={isRunning || isLoadingFromReport} />
                            </div>
                            <div className="min-w-0">
                                <CheckboxItem name="save_report" label="Save report" disabled={isRunning || isLoadingFromReport} />
                            </div>
                            <div className="min-w-0">
                                <CheckboxItem name="read_report" label="Read report" disabled={isRunning || isLoadingFromReport} />
                            </div>
                            <div className="min-w-0">
                                <CheckboxItem
                                    name="create_report_xlsx"
                                    label="Create inspection XLSX copies"
                                    disabled={isRunning || isLoadingFromReport}
                                />
                            </div>
                            <div className="min-w-0">
                                <CheckboxItem name="create_esg_input_xlsx" label="Create ESG input XLSX" disabled={isRunning || isLoadingFromReport} />
                            </div>
                            <div className="min-w-0">
                                <CheckboxItem name="create_lookthrough_csvs" label="Create lookthrough CSVs" disabled={isRunning || isLoadingFromReport} />
                            </div>
                        </div>

                        <div className="mt-6 flex flex-wrap gap-2">
                            <Button
                                variant="primary"
                                size="sm"
                                type="button"
                                disabled={isRunning || isLoadingFromReport || isSubmitting}
                                onClick={() => {
                                    void handleRunJob(values as FundParseFormValues);
                                }}
                            >
                                {isRunning ? "Running..." : "Run job"}
                            </Button>
                            <Button
                                variant="secondary"
                                size="sm"
                                type="button"
                                disabled={isRunning || isLoadingFromReport || isSubmitting}
                                onClick={() => {
                                    void handleLoadFromSavedReport(values as FundParseFormValues);
                                }}
                            >
                                {isLoadingFromReport ? "Loading..." : "Load from saved report"}
                            </Button>
                        </div>
                    </Form>
                )}
            </Formik>

            <div className="mt-8 rounded-lg border border-gray-200 bg-white p-4">
                <div className="mb-3">
                    <h2 className="text-lg font-medium">Position mapping</h2>
                </div>
                <FundParsePositionMappingTable summary={positionMappingSummary} />
            </div>

            <FundParseExposureCharts summary={exposureChartsSummary} />

            <div className="mt-8">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <h2 className="text-lg font-medium">Job console</h2>
                    {jobStatus ? (
                        <span className="text-sm text-gray-600">
                            Current status: <strong>{jobStatus}</strong>
                        </span>
                    ) : null}
                </div>
                <FundParseJobConsole lines={consoleLines} isRunning={isRunning || isLoadingFromReport} />
            </div>

            <FundParseOutputPanel
                outputFiles={outputFiles}
                manifest={outputManifest}
                isRunning={isRunning || isLoadingFromReport}
                onSelectFile={setSelectedOutputFile}
                onClear={() => {
                    setOutputFiles([]);
                    setSelectedOutputFile(null);
                    setAlert((current) => ({ ...current, visible: false }));
                }}
            />
        </div>
    );
}
